import { createAsyncThunk } from "@reduxjs/toolkit";
import API from "../../../network/API";
import { phpClient } from "../../../network";

export interface GetUnavailabilityDetailsRequest {
    unavailability_slot_id: number;
    date: string;
}

export interface GetUnavailabilityDetailsResponse {
    status: boolean;
    data?: {
        employee_unavailability_slot_id?: string;
        start_date?: string;
        end_date?: string;
        from_time?: string | null;
        to_time?: string | null;
        all_day?: string;
        repeating?: string;
        repeat_days?: string;
        note?: string;
        status?: string;
        department?: string;
        employee?: { employee_id?: string; first_name?: string; last_name?: string; image_url?: string };
        workflow_current_step_details?: { step_code?: string; current_status?: string | number; status_text?: string };
        workflow_details?: any[];
        /** Root-level status for the slot's date: 1 = Approved, -1 = Rejected, 0 or other = Pending */
        date_status?: number;
        /** Note (e.g. withdrawal note) for the slot's date */
        date_note?: string | null;
        extra_details_json?: { dates?: Record<string, { note?: string | null; status?: number }> };
        /** When present, user can open the notification detail screen (EMPLOYEE_UNAVAILABLITY). */
        notification_transaction_id?: string | null;
        [key: string]: any;
    };
    message?: string;
}

/** Fetch full unavailability details (including workflow/approval history) for the detail modal */
export async function getUnavailabilityDetails(
    params: GetUnavailabilityDetailsRequest
): Promise<GetUnavailabilityDetailsResponse> {
    const response = await phpClient.post<GetUnavailabilityDetailsResponse>({
        url: "/schedule/get_unavailability_details",
        data: params,
    });
    return response;
}

export interface UpdateUnavailabilityRequest {
    employee_unavailability_slot_id: number;
    date: string;
    status: number;
    notes: string;
}

export interface UpdateUnavailabilityResponse {
    status: boolean;
    data?: unknown;
    message?: string;
}

/** Update unavailability slot (e.g. withdraw: status -1, notes "Withdrawn by employee") */
export async function updateUnavailability(
    params: UpdateUnavailabilityRequest
): Promise<UpdateUnavailabilityResponse> {
    const response = await phpClient.post<UpdateUnavailabilityResponse>({
        url: "/schedule/update_unavailability",
        data: params,
    });
    return response;
}

interface CreateUnavailabilityPayload {
    start_date?: string | undefined;
    end_date?: string | undefined;
    from_time?: string | undefined;
    to_time?: string | undefined;
    all_day?: 0 | 1;
    repeating?: 0 | 1;
    repeat_days?: string | undefined;
    note?: string | undefined;
    overwrite_existing_slots?: 0 | 1;
}

export const createUnavailability = createAsyncThunk(
    "schedule/createUnavailability",
    async (payload: CreateUnavailabilityPayload, thunkAPI) => {
        const { rejectWithValue } = thunkAPI;

        try {
            const response = await phpClient.post<any>({
                url: "/schedule/create_unavailability",
                data: payload,
            });
            return response; // expects { status, data, message }
        } catch (error: any) {
            console.log(error, "error in createUnavailability");
            let message =
                (error && error.response && error.response.data && error.response.data.message) ||
                error?.message ||
                "An error occurred";

            // Strip leading "Error <code>: " if axiosClient.handleError added it
            if (typeof message === "string") {
                message = message.replace(/^Error\s*\d+\s*:\s*/i, "");
            }

            // Return a resolved object matching API shape so caller can handle status===false
            return {
                status: false,
                data: (error && error.response && error.response.data && error.response.data.data) || null,
                message,
            } as any;
        }
    }
);
